import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './App.css';

// Toast Provider
import { ToastProvider } from './components/Toast';
import { SubscriptionProvider } from './contexts/SubscriptionContext';

// Components
import Dashboard from './components/Dashboard';
import Students from './components/Students/Students';
import StudentForm from './components/Students/StudentForm';
import StudentDetail from './components/Students/StudentDetail';
import Batches from './components/Batches/Batches';
import BatchForm from './components/Batches/BatchForm';
import BatchDetail from './components/Batches/BatchDetail';
import Enrollments from './components/Enrollments/Enrollments';
import EnrollmentForm from './components/Enrollments/EnrollmentForm';
import Fees from './components/Fees/Fees';
import FeeForm from './components/Fees/FeeForm';
import FeeDetail from './components/Fees/FeeDetail';
import PaymentForm from './components/Fees/PaymentForm';
import Payments from './components/Payments/Payments';

// Invitation Components
import Invitations from './components/Invitations/Invitations';
import InvitationForm from './components/Invitations/InvitationForm';
import InvitationAccept from './components/Invitations/InvitationAccept';

// Enquiry Components
import Enquiries from './components/Enquiries/Enquiries';
import EnquiryForm from './components/Enquiries/EnquiryForm';
import QRCodeGenerator from './components/Enquiries/QRCodeGenerator';
import PublicEnquiryForm from './components/Enquiries/PublicEnquiryForm';

// Workshop Components
import Workshops from './components/Workshops/Workshops';
import WorkshopForm from './components/Workshops/WorkshopForm';
import WorkshopDetail from './components/Workshops/WorkshopDetail';
import WorkshopQRCode from './components/Workshops/WorkshopQRCode';
import WorkshopAttendanceQR from './components/Workshops/WorkshopAttendanceQR';
import WorkshopAttendance from './components/Workshops/WorkshopAttendance';
import PublicWorkshopForm from './components/Workshops/PublicWorkshopForm';
import PublicWorkshopAttendance from './components/Workshops/PublicWorkshopAttendance';
import TaskManager from './components/Tasks/TaskManager';
import OrganizationSignup from './components/Organizations/OrganizationSignup';
import OrganizationDetail from './components/Organizations/OrganizationDetail';

// Employee Components
import Employees from './components/Employees/Employees';
import EmployeeForm from './components/Employees/EmployeeForm';
import EmployeeDetail from './components/Employees/EmployeeDetail';

// Employee Attendance Components
import AttendanceManagement from './components/EmployeeAttendance/AttendanceManagement';
import AttendanceRecords from './components/EmployeeAttendance/AttendanceRecords';
import PublicAttendance from './components/EmployeeAttendance/PublicAttendance';

// Student Attendance Components
import StudentAttendanceManagement from './components/StudentAttendance/AttendanceManagement';
import StudentAttendanceRecords from './components/StudentAttendance/AttendanceRecords';
// StudentAttendanceAnalytics is lazy-loaded below
import PublicStudentAttendance from './components/StudentAttendance/PublicAttendance';

// Test Components
// TestManagement, CreateTest, TestDetails, PublicTest are lazy-loaded below
import TopPerformers from './components/TopPerformers';

// Auth Components
import Login from './components/Auth/Login';
import AdminForgotPassword from './components/Auth/AdminForgotPassword';
import AdminVerifyOTP from './components/Auth/AdminVerifyOTP';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import RoleProtectedRoute from './components/Auth/RoleProtectedRoute';

// Student Portal Components
import StudentLogin from './components/Student/Auth/StudentLogin';
import StudentProtectedRoute from './components/Student/Auth/StudentProtectedRoute';
import ChangePassword from './components/Student/Auth/ChangePassword';
import ForgotPassword from './components/Student/Auth/ForgotPassword';
import VerifyOTP from './components/Student/Auth/VerifyOTP';
import StudentDashboard from './components/Student/Dashboard/StudentDashboard';
import StudentAttendance from './components/Student/Attendance/StudentAttendance';
import StudentTests from './components/Student/Tests/StudentTests';
import StudentProfile from './components/Student/Profile/StudentProfile';
import StudentTopPerformers from './components/Student/TopPerformers/StudentTopPerformers';
import StudentChatInterface from './components/Student/Chat/StudentChatInterface';
// AIAssistantPage is lazy-loaded below
// import StudentFees from './components/Student/Fees/StudentFees'; // Hidden for future use

// Profile Component
import Profile from './components/Profile/Profile';

// Layout Component
import Layout from './components/Layout';

// Loading Spinner for lazy-loaded components
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

// Lazy-loaded heavy components
const StudentAttendanceAnalytics = lazy(() => import('./components/StudentAttendance/AttendanceAnalytics'));
const TestManagement = lazy(() => import('./components/Tests/TestManagement'));
const CreateTest = lazy(() => import('./components/Tests/CreateTest'));
const TestDetails = lazy(() => import('./components/Tests/TestDetails'));
const PublicTest = lazy(() => import('./components/Tests/PublicTest'));
const AIAssistantPage = lazy(() => import('./components/Student/AIChat').then(module => ({ default: module.AIAssistantPage })));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,      // 5 minutes
      cacheTime: 10 * 60 * 1000,     // 10 minutes
      refetchOnMount: false,
    },
  },
});

function App() {
  return (
            <QueryClientProvider client={queryClient}>
          <ToastProvider>
            <SubscriptionProvider>
              <Router>
                <div className="App min-h-screen bg-gray-800">
            <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/signup" element={<OrganizationSignup />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/verify-otp" element={<AdminVerifyOTP />} />
              <Route path="/invite/:token" element={<InvitationAccept />} />
              
              {/* Protected Routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/students" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Students />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/students/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/students/:id" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/students/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/batches" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Batches />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/batches/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <BatchForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/batches/:id" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <BatchDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/batches/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <BatchForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enrollments" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Enrollments />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enrollments/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EnrollmentForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enrollments/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EnrollmentForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Fees />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <FeeForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees/:feeStructureId" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <FeeDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees/:feeStructureId/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <FeeForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees/:feeStructureId/payments" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <FeeDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/fees/:feeStructureId/payments/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <PaymentForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/payments" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Payments />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/invitations" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Invitations />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/invitations/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <InvitationForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/profile" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Profile />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Organization Routes */}
              <Route path="/organization" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <OrganizationDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Enquiry Routes */}
              <Route path="/enquiries" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Enquiries />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enquiries/create" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EnquiryForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enquiries/:id" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EnquiryForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enquiries/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EnquiryForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/enquiries/qr-code" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <QRCodeGenerator />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              {/* Workshop Routes */}
              <Route path="/workshops" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Workshops />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/:id" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/:id/qr-code" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopQRCode />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/:id/attendance-qr" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopAttendanceQR />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              <Route path="/workshops/:id/attendance" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <WorkshopAttendance />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />
              
              {/* Public Workshop Form Routes */}
              <Route path="/workshop/:publicLinkId" element={<PublicWorkshopForm />} />
              
              {/* Public Workshop Attendance Routes */}
              <Route path="/workshop-attendance/:qrCodeId" element={<PublicWorkshopAttendance />} />

              {/* Public Employee Attendance Routes */}
              <Route path="/employee-attendance/:qrCodeId" element={<PublicAttendance />} />

              {/* Public Student Attendance Routes */}
              <Route path="/student-attendance/:qrCodeId" element={<PublicStudentAttendance />} />

              {/* Public Test Routes */}
              <Route path="/test/:testId" element={<PublicTest />} />

              {/* Task Manager Routes */}
              <Route path="/tasks" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <TaskManager />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Employee Routes */}
              <Route path="/employees" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <Employees />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/employees/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EmployeeForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/employees/:id/edit" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EmployeeForm />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/employees/:id" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <EmployeeDetail />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Employee Attendance Routes */}
              <Route path="/employee-attendance" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <AttendanceManagement />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/employee-attendance/records" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <AttendanceRecords />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Student Attendance Routes */}
              <Route path="/student-attendance" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentAttendanceManagement />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/student-attendance/records" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentAttendanceRecords />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/student-attendance/analytics" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <StudentAttendanceAnalytics />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Test Management Routes */}
              <Route path="/tests" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <TestManagement />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/tests/new" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <CreateTest />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/tests/:testId" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <TestDetails />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              <Route path="/top-performers" element={
                <ProtectedRoute>
                  <RoleProtectedRoute>
                    <Layout>
                      <TopPerformers />
                    </Layout>
                  </RoleProtectedRoute>
                </ProtectedRoute>
              } />

              {/* Public Enquiry Form Routes */}
              <Route path="/enquiry-form" element={<PublicEnquiryForm />} />
              <Route path="/enquiry-form/:qrCodeId" element={<PublicEnquiryForm />} />

              {/* Student Portal Routes */}
              <Route path="/student/login" element={<StudentLogin />} />
              <Route path="/student/forgot-password" element={<ForgotPassword />} />
              <Route path="/student/verify-otp" element={<VerifyOTP />} />

              <Route path="/student/dashboard" element={
                <StudentProtectedRoute>
                  <StudentDashboard />
                </StudentProtectedRoute>
              } />

              <Route path="/student/change-password" element={
                <StudentProtectedRoute>
                  <ChangePassword />
                </StudentProtectedRoute>
              } />

              <Route path="/student/attendance" element={
                <StudentProtectedRoute>
                  <StudentAttendance />
                </StudentProtectedRoute>
              } />

              <Route path="/student/tests" element={
                <StudentProtectedRoute>
                  <StudentTests />
                </StudentProtectedRoute>
              } />

              <Route path="/student/profile" element={
                <StudentProtectedRoute>
                  <StudentProfile />
                </StudentProtectedRoute>
              } />

              <Route path="/student/top-performers" element={
                <StudentProtectedRoute>
                  <StudentTopPerformers />
                </StudentProtectedRoute>
              } />

              <Route path="/student/chat" element={
                <StudentProtectedRoute>
                  <StudentChatInterface />
                </StudentProtectedRoute>
              } />

              <Route path="/student/ai-assistant" element={
                <StudentProtectedRoute>
                  <AIAssistantPage />
                </StudentProtectedRoute>
              } />

              {/* Fees route hidden but component kept intact */}
              {/* <Route path="/student/fees" element={
                <StudentProtectedRoute>
                  <StudentFees />
                </StudentProtectedRoute>
              } /> */}

            </Routes>
            </Suspense>
          </div>
        </Router>
        </SubscriptionProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}

export default App; 